import Logo from "@/assets/icons/logo.svg?react";
import { Box, CssBaseline, Link, Typography } from "@mui/material";
import { FC } from "react";

import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { FormEvent, useState } from "react";
import { z } from "zod";
import { useLogin } from "../../queries";
import { ROUTE, formatEmail } from "@/utils";
import { Link as RouterLink } from "react-router-dom";

const initialFormState = {
  email: "",
  password: "",
};

const formSchema = z.object({
  email: z.string().min(1, "Field is required").email("Wrong format"),
  password: z.string().min(1, "Field is required"),
});

type FormData = z.infer<typeof formSchema>;

const LoginPage: FC = () => {
  const { login, isLoginLoading } = useLogin();

  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({
    fullName: false,
    email: false,
    phoneNumber: false,
    password: false,
  });
  const [adminUserFormData, setAdminUserFormData] = useState<Partial<FormData>>(
    {},
  );

  const formData: FormData = {
    ...initialFormState,
    ...adminUserFormData,
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    login(formData);
  };
  const validate = () => {
    const res = formSchema.safeParse(formData);
    if (res.success) {
      return undefined;
    }
    return res.error.format();
  };

  const errors = validate();
  const isFormFilled = Object.values(formData).every((v) => !!v);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        p: 2,
        pt: 20,
        alignItems: "center",
        margin: "0 auto",
        maxWidth: "500px",
      }}
    >
      <CssBaseline />
      <Logo width={"100%"} />

      <Box component="form" width={"100%"} onSubmit={onSubmit}>
        <Stack justifyContent={"flex-start"} width={"100%"}>
          <Typography variant="h4" mt={4}>
            Login to your account!
          </Typography>
          <Typography variant="body2" sx={{ mb: 4 }}>
            Enter your credentials to access admin features
          </Typography>
        </Stack>

        <Stack
          style={{ padding: 0 }}
          direction={{ xs: "column", sm: "row" }}
          spacing={4}
        >
          <TextField
            fullWidth
            variant="filled"
            autoComplete="email"
            sx={{ mb: 2 }}
            required
            placeholder="Email"
            label={
              touched.email && errors?.email
                ? errors?.email?._errors[0]
                : "Email"
            }
            onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
            error={touched.email ? !!errors?.email?._errors.length : false}
            value={formData.email}
            onChange={(e) => {
              setAdminUserFormData((prev) => ({
                ...prev,
                email: formatEmail(e.target.value),
              }));
              if (touched.email) {
                validate();
              }
            }}
            slotProps={{
              htmlInput: {
                autoCapitalize: "none",
              },
            }}
          />
          <TextField
            fullWidth
            variant="filled"
            autoComplete="password"
            type={showPassword ? "text" : "password"}
            placeholder="password"
            label={
              touched.password && errors?.password
                ? errors?.password?._errors[0]
                : "Password"
            }
            onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
            error={
              touched.password ? !!errors?.password?._errors.length : false
            }
            value={formData.password}
            onChange={(e) => {
              setAdminUserFormData((prev) => ({
                ...prev,
                password: e.target.value,
              }));
              if (touched.password) {
                validate();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
        <Box marginTop={1} marginBottom={2} textAlign={"right"}>
          <Link
            component={RouterLink}
            to={ROUTE.AUTH_PASSWORD_RESET}
            underline="none"
            sx={{
              color: "#FFFFFF",
              textDecoration: "none",
            }}
          >
            Forgot Password
          </Link>
        </Box>
        <Button
          fullWidth
          color="secondary"
          variant="contained"
          type="submit"
          loading={isLoginLoading}
          disabled={isLoginLoading || !isFormFilled || !!errors}
        >
          Log In
        </Button>
      </Box>

      <Box
        sx={{
          position: "absolute",
          left: 0,
          width: "100%",
          display: "flex",
          justifyContent: "center",
          bottom: 10,
        }}
      >
        <Typography variant="body2" align="center">
          © 2024 Société.&nbsp;|&nbsp;
          <Link
            href="https://platform.societe.app/terms-and-privacy"
            color="inherit"
            underline="hover"
            target="_blank"
          >
            Privacy Policy
          </Link>{" "}
          &nbsp;|&nbsp;
          <Link
            href="https://platform.societe.app/terms-of-use"
            color="inherit"
            underline="hover"
            target="_blank"
          >
            Terms Of Use
          </Link>{" "}
          &nbsp;|&nbsp;
          <Link
            href="mailto:info@societe.com"
            underline="hover"
            target="_blank"
          >
            info@societe.com
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export { LoginPage };
